class PaymentInstallmentsSelectionViewController extends BaseTableViewController
{
    static newInstance(): PaymentInstallmentsSelectionViewController {
        let vc = new PaymentInstallmentsSelectionViewController( "payment-installments-selection-view" );
        vc.initWithResource( "layout/paymentinstallments/PaymentInstallmentsSelectionView.html" );
        return vc;
    }

    private cancelButton:MUIButton = null;
    private generateButton:MUIButton = null;

    private fileNameTextField:MUITextField = null;
    private dueDateTextField:MUITextField = null;
    private dueDateButton:MUIButton = null;
    private bankAccountComboBox:MUIComboBox = null;
    private sepaTypeComboBox:MUIComboBox = null;

    private totalLabel:MUILabel = null;
    private selectedTotalLabel:MUILabel = null;

    private beginDateString:string = null;
    private endDateString:string = null;

    private dueDate:Date = null;
    private bankAccounts:BankAccount[] = [];
    private selectedItems:any = {};

    get preferredContentSize() {
        return new MIOSize(800, 800);
    }

    viewDidLoad() {
        super.viewDidLoad();

        this.cancelButton = MUIOutlet( this, "cancel-btn", "MUIButton" );
        if (this.cancelButton != null) this.cancelButton.setAction( this, function(this:PaymentInstallmentsSelectionViewController){
            this.dismissViewController( true );
        });

        this.generateButton = MUIOutlet( this, "generate-btn", "MUIButton" );
        if (this.generateButton != null) this.generateButton.setAction( this, function(this:PaymentInstallmentsSelectionViewController){
            this.generate();
        });

        this.fileNameTextField = MUIOutlet( this, "file-name-tf", "MUITextField" );

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        this.dueDateTextField = MUIOutlet( this, "due-date-tf", "MUITextField" );
        if (this.dueDateTextField != null) {
            this.dueDateTextField.setOnChangeText( this, function(this:PaymentInstallmentsSelectionViewController, control:MUITextField, value:string){
                this.dueDate = ad.dateFormatter.dateFromString( value );
            });
        }

        this.dueDateButton = MUIOutlet( this, "due-date-btn", "MUIButton" );
        if (this.dueDateButton != null) this.dueDateButton.setAction( this, function(this:PaymentInstallmentsSelectionViewController){
            let dp = new MUIDatePickerController();
            dp.init();
            dp.delegate = this;
            dp.modalPresentationStyle = MUIModalPresentationStyle.Popover;
            let pc = dp.popoverPresentationController;
            pc.sourceRect = this.dueDateButton.frame;
            pc.sourceView = this.dueDateButton;
            this.presentViewController( dp, true );
        });

        this.sepaTypeComboBox = MUIOutlet( this, "sepa-type-cb", "MUIComboBox" );
        if (this.sepaTypeComboBox != null) {
            this.sepaTypeComboBox.removeAllItems();
            this.sepaTypeComboBox.addItem( "SEPA CORE", 0 );
            this.sepaTypeComboBox.addItem( "SEPA B2B", 1 );
            this.sepaTypeComboBox.selectItem( 0 );
        }

        this.bankAccountComboBox = MUIOutlet( this, "bank-account-cb", "MUIComboBox" );
        if (this.bankAccountComboBox != null) {
            let sds = [MIOSortDescriptor.sortDescriptorWithKey( "info.title", true)]
            let predicate = MIOPredicate.predicateWithFormat("deletedAt = null")
            DBHelper.queryObjectsWithCompletion( "BankAccount", sds, predicate, ["info", "bank"], this, function(this:PaymentInstallmentsSelectionViewController, objects:BankAccount[]){
                this.bankAccounts = objects ?? [];
                this.bankAccountComboBox.removeAllItems();
                for (let i = 0; i < this.bankAccounts.length; i++) {
                    this.bankAccountComboBox.addItem( this.bankAccounts[i].bank.name + " - " + this.bankAccounts[i].info.title, i );
                }
                if (this.bankAccounts.length > 0) this.bankAccountComboBox.selectItem( 0 );
            });
        }

        this.totalLabel = MUIOutlet( this, "footer-total-lbl", "MUILabel" );
        this.selectedTotalLabel = MUIOutlet( this, "footer-selected-total-lbl", "MUILabel" );

        this.tableView = MUIOutlet( this, "table-view", "UITableView" );
        this.tableView.dataSource = this;
        this.tableView.delegate = this;
    }

    setDateRange( beginDateString:string, endDateString:string ) {
        this.beginDateString = beginDateString;
        this.endDateString = endDateString;
    }

    didSelectDate( datePickerController:MUIDatePickerController, date:Date ) {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        this.dueDate = date;
        if (this.dueDateTextField != null) this.dueDateTextField.text = ad.dateFormatter.stringFromDate( date );
    }

    cellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath) {
        let item = this.fetchedResultsController.objectAtIndexPath( indexPath ) as PaymentInstallment;
        let cell = tableView.dequeueReusableCellWithIdentifier( "PaymentInstallmentsSelectionCell" ) as PaymentInstallmentsSelectionCell;
        cell.item = item;
        cell.itemSelected = this.isSelected( item );
        return cell;
    }

    didSelectCellAtIndexPath( tableView:UITableView, indexPath:MIOIndexPath ) {
        let item = this.fetchedResultsController.objectAtIndexPath( indexPath ) as PaymentInstallment;
        this.toggleSelection( item );
        this.tableView.reloadData();
        this.updateTotals();
    }

    get fetchedResultsController() {
        if (this._fetchedResultsController != null) return this._fetchedResultsController;

        let ad = MUIWebApplication.sharedInstance().delegate;

        let sortDescriptors = [ MIOSortDescriptor.sortDescriptorWithKey( "date", true ), MIOSortDescriptor.sortDescriptorWithKey( "legalEntity.name", true ) ];

        let predicateFormat = "date >= '" + this.beginDateString + "' AND date <= '" + this.endDateString + "'"
            + " AND status == " + PaymentInstallmentStatus.pending
            + " AND paymentMethod.type == " + PayMethodType.SEPA;

        let fetchRequest = DBHelper.listFetchRequestWithEntityName( "LegalEntityPaymentInstallment", sortDescriptors, predicateFormat );
        fetchRequest.relationshipKeyPathsForPrefetching = [ "paymentMethod", "invoice" ];

        let fetchedResultsController = new MIOFetchedResultsController();
        fetchedResultsController.initWithFetchRequest( fetchRequest, ad.managedObjectContext, null );
        fetchedResultsController.delegate = this;
        fetchedResultsController.performFetch();

        this._fetchedResultsController = fetchedResultsController;

        this.selectAllByDefault();
        this.updateTotals();

        return this._fetchedResultsController;
    }

    private isSelected( item:PaymentInstallment ): boolean {
        return this.selectedItems[ item.identifier ] != null;
    }

    private toggleSelection( item:PaymentInstallment ) {
        if (this.selectedItems[ item.identifier ] != null) delete this.selectedItems[ item.identifier ];
        else this.selectedItems[ item.identifier ] = item;
    }

    private selectAllByDefault() {
        this.selectedItems = {};
        for (let s = 0; s < this._fetchedResultsController.sections.length; s++) {
            let section = this._fetchedResultsController.sections[s];
            for (let r = 0; r < section.numberOfObjects(); r++) {
                let ip = MIOIndexPath.indexForRowInSection( r, s );
                let item = this._fetchedResultsController.objectAtIndexPath( ip ) as PaymentInstallment;
                this.selectedItems[ item.identifier ] = item;
            }
        }
    }

    private getSelectedItems(): PaymentInstallment[] {
        let result:PaymentInstallment[] = [];
        for (let key in this.selectedItems) {
            if (this.selectedItems[ key ] != null) result.push( this.selectedItems[ key ] );
        }
        return result;
    }

    private updateTotals() {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        let total = 0;
        let selectedTotal = 0;
        for (let s = 0; s < this._fetchedResultsController.sections.length; s++) {
            let section = this._fetchedResultsController.sections[s];
            for (let r = 0; r < section.numberOfObjects(); r++) {
                let ip = MIOIndexPath.indexForRowInSection( r, s );
                let item = this._fetchedResultsController.objectAtIndexPath( ip ) as PaymentInstallment;
                total += item.amount;
                if (this.isSelected( item )) selectedTotal += item.amount;
            }
        }

        if (this.totalLabel != null) this.totalLabel.text = ad.currencyFormatter.stringFromNumber( total );
        if (this.selectedTotalLabel != null) this.selectedTotalLabel.text = ad.currencyFormatter.stringFromNumber( selectedTotal );
    }

    private generate() {
        let items = this.getSelectedItems();
        if (items.length == 0) {
            AppHelper.showErrorMessage( null, MIOLocalizeString( "ERROR", "ERROR" ), MIOLocalizeString( "SELECT_AT_LEAST_ONE_INSTALLMENT", "SELECT AT LEAST ONE INSTALLMENT" ) );
            return;
        }

        if (this.dueDate == null) {
            AppHelper.showErrorMessage( null, MIOLocalizeString( "ERROR", "ERROR" ), MIOLocalizeString( "PLEASE_SELECT_A_DUE_DATE", "PLEASE SELECT A DUE DATE" ) );
            return;
        }

        let bankIndex = this.bankAccountComboBox != null ? parseInt( this.bankAccountComboBox.getSelectedItem() ) : -1;
        let account = (bankIndex >= 0 && bankIndex < this.bankAccounts.length) ? this.bankAccounts[ bankIndex ] : null;
        if (account == null) {
            AppHelper.showErrorMessage( null, MIOLocalizeString( "ERROR", "ERROR" ), MIOLocalizeString( "PLEASE_SELECT_A_BANK_ACCOUNT", "PLEASE SELECT A BANK ACCOUNT" ) );
            return;
        }

        let typeIndex = this.sepaTypeComboBox != null ? parseInt( this.sepaTypeComboBox.getSelectedItem() ) : 0;
        let sepaType = typeIndex == 1 ? "19.44" : "19.14";

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let dueDateString = ad.serverDateFormatter.stringFromDate( this.dueDate );

        let year = this.dueDate.getFullYear().toString();
        let month = ("0" + (this.dueDate.getMonth() + 1)).slice( -2 );
        let bankName = (account.bank != null && account.bank.name != null ? account.bank.name : "").replace( /\s+/g, "_" );
        let accountNumber = account.info != null && account.info.number != null ? account.info.number : "";
        let last4 = accountNumber.replace( /\s+/g, "" ).slice( -4 );

        let extra = this.fileNameTextField != null && this.fileNameTextField.text != null ? this.fileNameTextField.text.trim() : "";
        let baseName = year + "-" + month + "_" + bankName + "_" + last4 + (extra.length > 0 ? "_" + extra : "");
        let filename = baseName.toLowerCase().endsWith( ".txt" ) ? baseName : (baseName + ".txt");

        ad.webService.generateSEPAFileFromInstallments( items, sepaType, account, dueDateString, filename, extra, null );

        this.dismissViewController( true );
    }
}
